import type { ShipTime } from '@felix/domain';
import { Text, UnstyledButton } from '@mantine/core';
import { IconArrowBackUp, IconBan } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { CASH_SECTION_PATHS } from '../cash/CashScreen.js';
import { DAILY_REPORTS_PATH } from '../daily/DailyPlanScreen.js';
import { defectsPath } from '../defects/routes.js';
import { WarningAlert } from '../forms/FormRecordParts.js';
import classes from '../forms/Forms.module.css';
import { recordPath } from '../forms/record-model.js';
import { recordTime, utcOffsetLabel } from '../forms/status.js';
import { hsePath } from '../hse/routes.js';
import work from '../pms/PmsWork.module.css';
import { requisitionPath } from '../purchasing/requisitions.js';
import { raFindingsPath } from '../risk/findings/view.js';
import type { ReturnNotice } from './types.js';

/**
 * İade ve ret bildirimi gönderene (ADR 0116 §1 ve Ek): ISM formu için kurulan desen bütün onay
 * akışlarında. Kayıt sayfasında bant ({@link ReturnedBanner}), zilde madde
 * ({@link ReturnedBellList}) ve panoda "Returned to you" ({@link ReturnedCard}). Veri
 * `packages/db` `returned.ts`'ten (saklanmaz, türetilir).
 */

export type { ReturnNotice } from './types.js';

export type ReturnedModule =
  | 'ism_forms'
  | 'work_records'
  | 'postponements'
  | 'defects'
  | 'hse_reports'
  | 'daily_reports'
  | 'cash'
  | 'risk_assessments'
  | 'ra_operations'
  | 'purchasing'
  | 'documents'
  | 'ra_library';

export type ReturnedVariant =
  | 'record'
  | 'postponement_revoked'
  | 'verification_rejected'
  | 'finding_escalation'
  | 'finding_change_request'
  | 'cash_reconciliation'
  | 'hse_non_conformity'
  | 'hse_deficiency'
  | 'hse_near_miss'
  | 'hse_accident'
  | 'document_ism'
  | 'document_manuals';

/** Zil ve pano maddesi (`DashboardSummary.returnedToMe`). */
export interface ReturnedNotice {
  kind: 'record_returned';
  module: ReturnedModule;
  variant: ReturnedVariant;
  recordId: string;
  targetId: string;
  recordNo: string;
  title: string;
  vesselId: string | null;
  returnedAt: string;
  returnedBy: string;
  returnedRole: string | null;
  reason: string | null;
  office: boolean;
  final: boolean;
}

const RISK_ASSESSMENT_PATH = '/compliance/risk-assessment';
const RA_VERIFICATION_PATH = '/compliance/risk-assessment/verification';
const RA_LIBRARY_PATH = '/compliance/risk-assessment/library';
const POSTPONEMENTS_PATH = '/pms/postponements';

const HSE_TYPES = {
  hse_non_conformity: 'non_conformity',
  hse_deficiency: 'deficiency',
  hse_near_miss: 'near_miss',
  hse_accident: 'accident',
} as const;

/** Maddenin açtığı sayfa: kaydın kendi ekranı (gemide ve ofiste aynı yollar). */
export function returnedPath(
  item: Pick<ReturnedNotice, 'module' | 'variant' | 'recordId' | 'targetId'>,
): string {
  const id = encodeURIComponent(item.targetId);
  switch (item.module) {
    case 'ism_forms':
      return recordPath(item.recordId);
    case 'work_records':
      return `/pms/work-orders?wo=${id}`;
    case 'postponements':
      return `${POSTPONEMENTS_PATH}?p=${id}`;
    case 'defects':
      return defectsPath({ view: 'detail', id: item.targetId });
    case 'hse_reports': {
      const type = HSE_TYPES[item.variant as keyof typeof HSE_TYPES] ?? null;
      return hsePath(type, { view: 'detail', id: item.targetId, tab: null });
    }
    case 'daily_reports':
      return `${DAILY_REPORTS_PATH}?report=${id}`;
    case 'cash':
      return item.variant === 'cash_reconciliation'
        ? CASH_SECTION_PATHS.reconciliation
        : CASH_SECTION_PATHS['ctm-requests'];
    case 'risk_assessments':
      return RISK_ASSESSMENT_PATH;
    case 'ra_operations':
      return item.variant === 'finding_escalation' || item.variant === 'finding_change_request'
        ? raFindingsPath({ view: 'detail', id: item.targetId })
        : `${RA_VERIFICATION_PATH}?id=${id}`;
    case 'purchasing':
      return requisitionPath({ view: 'detail', id: item.targetId });
    case 'documents':
      return `${item.variant === 'document_manuals' ? '/compliance/manuals' : '/compliance/ism-forms'}?doc=${id}`;
    case 'ra_library':
      return RA_LIBRARY_PATH;
  }
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** "Master Ahmet Kaya: <gerekçe>" (iade eden ve gerekçe). */
export function returnedNoticeLine(t: Translate, item: ReturnedNotice): string {
  const by = [item.returnedRole, item.returnedBy].filter(Boolean).join(' ') || '—';
  return t('forms.returned.by', {
    by,
    reason: item.reason?.trim() ? item.reason : t('forms.returned.noReason'),
  });
}

function bannerTime(at: string, offsetMin: number | null | undefined): string {
  if (offsetMin === null || offsetMin === undefined) return recordTime(at, null);
  const text = formatDateTime(at, offsetMin);
  return text === '—' ? text : `${text} (${utcOffsetLabel(offsetMin)})`;
}

/**
 * Kayıt sayfasının üstündeki bant (ISM formundaki "Returned for correction" ile aynı görünüm):
 * kararı veren (rol ve ad), zaman, gerekçe. Kesin retlerde "Rejected" (ofisin erteleme geri
 * çevirmesinde "Revoked by the office", kasa sorgusunda "Queried by the office"). `hint`:
 * düzeltip yeniden gönderebilecek kişiye ipucu. Zaman gemide kaydın saat farkıyla, verilmezse UTC.
 */
export function ReturnedBanner({
  notice,
  offsetMin,
  timeZone,
  hint = false,
  kind,
}: {
  notice: ReturnNotice | null | undefined;
  /** Kaydın saat farkı (dakika). */
  offsetMin?: number | null | undefined;
  /** Gemi saati kaydı (ADR 0065); verilirse saat farkı karar anına göre. */
  timeZone?: ShipTime | null | undefined;
  hint?: boolean;
  kind?: 'revoked' | 'queried';
}) {
  const { t } = useTranslation();
  if (!notice) return null;
  const by = [notice.role, notice.by].filter(Boolean).join(' ') || '—';
  const at = timeZone ? recordTime(notice.at, timeZone) : bannerTime(notice.at, offsetMin);
  const verb = notice.final ? 'rejected' : 'returned';
  return (
    <WarningAlert
      icon={notice.final ? <IconBan size={18} /> : <IconArrowBackUp size={18} />}
      className={classes.notice}
      title={t(`returned.title.${kind ?? verb}`)}
      data-returned-banner
    >
      <Text size="sm">
        {t(notice.office ? `returned.text.${verb}Office` : `returned.text.${verb}`, { by, at })}
      </Text>
      {notice.reason ? (
        <Text size="sm" fw={600}>
          {t('returned.reason', { reason: notice.reason })}
        </Text>
      ) : null}
      {hint ? (
        <Text size="sm">{t(notice.final ? 'returned.finalHint' : 'returned.hint')}</Text>
      ) : null}
    </WarningAlert>
  );
}

/** Zilde kişiye dönen iadeler ve retler (ISM formu dışındakiler; formlar `FormBellList`'te). */
export function ReturnedBellList({
  items,
  onOpen,
}: {
  items: readonly ReturnedNotice[];
  onOpen: (item: ReturnedNotice) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <ul className={work.bellList}>
      {items.map((item) => (
        <li key={`returned-${item.module}-${item.recordId}`}>
          <UnstyledButton className={work.bellItem} onClick={() => onOpen(item)}>
            <span className={work.bellIcon} aria-hidden>
              {item.final ? <IconBan size={16} /> : <IconArrowBackUp size={16} />}
            </span>
            <span>
              <strong>
                {t(item.final ? 'returned.bell.rejected' : 'returned.bell.returned', {
                  record: item.recordNo || t(`returned.modules.${item.module}`),
                })}
              </strong>
              <span className={work.note}>
                {[t(`returned.modules.${item.module}`), item.title].filter(Boolean).join(' · ')}
              </span>
              <span className={work.note}>{returnedNoticeLine(t as Translate, item)}</span>
            </span>
          </UnstyledButton>
        </li>
      ))}
    </ul>
  );
}

/** Kartta gösterilen en çok satır. */
const LIMIT = 5;

/**
 * Panoda "Returned to you" (ADR 0116 §1 ve Ek): kişinin girdiği ya da gönderdiği, iade edilmiş ya
 * da reddedilmiş kayıtlar (ISM formu ve diğer akışlar); gerekçe ve karar veren. Yoksa çizilmez.
 */
export function ReturnedCard({
  items,
  timeZone,
}: {
  items: readonly ReturnedNotice[] | undefined;
  timeZone: ShipTime | null;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  if (!items || items.length === 0) return null;
  return (
    <Panel title={t('forms.returned.title')} icon={<IconArrowBackUp size={17} stroke={1.8} />}>
      <ul className={classes.approvalCardList}>
        {items.slice(0, LIMIT).map((item) => {
          const path = returnedPath(item);
          const label = item.recordNo || t(`returned.modules.${item.module}`);
          const title =
            item.module === 'ism_forms'
              ? item.title
              : [t(`returned.modules.${item.module}`), item.title].filter(Boolean).join(' · ');
          return (
            <li
              key={`returned-${item.module}-${item.recordId}`}
              className={classes.approvalCardItem}
              data-overdue="true"
            >
              <a
                href={path}
                className={classes.recordLink}
                onClick={(event) => shell && followLink(event, path, shell.navigate)}
              >
                {label}
              </a>
              <span className={classes.approvalCardText} title={title}>
                {title}
              </span>
              <span className={classes.muted}>
                {returnedNoticeLine(t as Translate, item)} · {recordTime(item.returnedAt, timeZone)}
              </span>
              <StatusBadge tone="danger">
                {item.module === 'ism_forms'
                  ? t('forms.status.returned')
                  : t(item.final ? 'returned.badge.rejected' : 'returned.badge.returned')}
              </StatusBadge>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
